import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Box,
  Link,
  Chip,
  CircularProgress,
  Tooltip,
  DialogTitle,
  DialogContentText,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/system";
import * as Yup from "yup";
import { useFormik } from "formik";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { handleResumeAction, ResumeActionLink } from "./CandidateTable";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCandidateStatus,
  updateResumeDownloadLimit,
} from "../../../features/Recruiter/candidateSlice";
import CandidateStatusButton from "../CandidateDetails/CandidateStatusButton";
import { CloseFullscreen } from "@mui/icons-material";
import { notifyError, notifySuccess } from "../../../utils/notificationMsg";
import CandidateBonusDialog from "../CandidateDetails/CandidateBonusDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BadgeIcon from "@mui/icons-material/Badge";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { MdEmail } from "react-icons/md";
import { FaPhoneAlt } from "react-icons/fa";
import dayjs from "dayjs";
import {
  formatAmount,
  formatUSAmount,
  scrollToTop,
} from "../../../utils/helpers";
import ViewStatus from "../../Admin/Lists/ViewStatus";
import { MEDIA_URL } from "../../../api/endpoints";
import AnimatedDialog from "../../../components/Auth/OTPVerification/AnimatedDialog";
import { CustomButton } from "../../../components/common/LoadingButton";
import {
  changeStatus,
  getallcandidates,
} from "../../../features/Admin/RecruiterSlice";
import Loader from "../../../components/common/Loader/Loader";
import {
  candidateTotalAmount,
  userTextValidation,
} from "../../../components/common/Formik/CustomSchema";
import ClientDetailsDialogue from "../../Admin/Lists/ClientDetailsDialogue";
import ClientDetailsDialogueForInprocessCandidate from "../../Admin/Lists/ClientDetailsDialogueForInprocessCandidate";

const SectionTitle = styled(Typography)(({ theme }) => ({
  backgroundColor: "#F0F8FF",
  padding: theme.spacing(1),
  marginBottom: theme.spacing(2),
  fontWeight: "bold",
}));
const InfoContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  margin: "0",
  padding: "30px",
  paddingLeft: "100px",
  backgroundColor: "#ccc2f5",
  borderRadius: "0",
  position: "relative",
  left: "50%",
  transform: "translateX(-50%)",
}));
const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "15px",
  fontFamily: "Urbane-Light",
  marginBottom: theme.spacing(2),
  fontWeight: "600",
}));
const Info = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  fontSize: "22px",
  fontFamily: "Urbane-Light",
  fontWeight: 700,
}));
const InfoValue = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  fontSize: "15px",
  fontFamily: "Urbane-Light",
  fontWeight: 500,
  color: "#363636",
}));
const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  minWidth: "150px",
  borderRadius: "8px",
  textTransform: "none",
}));

const RightInfoContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ccc2f5",
  padding: theme.spacing(3),
  marginTop: theme.spacing(4),
  borderRadius: "5px",
  width: "100%",
  position: "relative",
  [theme.breakpoints.up("md")]: {
    width: "100%",
  },
  boxShadow: theme.shadows[2],
}));

const RightInfoItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column", // Icon and label in one row, value below
  marginBottom: theme.spacing(3), // Add spacing between items here
  fontFamily: "Urbane-Light",
}));

const RightInfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center", // Align icon and label in a single row
}));

const RightInfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "15px",
  color: "#202124",
  fontFamily: "Urbane-Light",
  fontWeight: "600",
  marginLeft: theme.spacing(1), // Space between icon and label
}));

const RightInfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "15px",
  fontFamily: "Urbane-Light",
  fontWeight: 500,
  color: "#363636",
  marginTop: theme.spacing(0.5), // Space between label and value
  marginLeft: theme.spacing(4), // Optional alignment for value
}));

const EducationSectionContainer = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(5),
  marginLeft: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: "#e3e3e3",
  borderRadius: "8px",
  justifyContent: "left",
  boxShadow: theme.shadows[2],
  flexGrow: 1,
}));

const StatusContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginLeft: theme.spacing(1),
  boxShadow: theme.shadows[4], // No need for the theme function here
  padding: theme.spacing(5),
  backgroundColor: "#e3e3e3",
  borderRadius: "8px",
  display: "flex", // Ensure this is a flex container
  justifyContent: "flex-end", // Align children to the left
}));

const EducationTitle = styled(Typography)({
  fontSize: "20px",
  marginBottom: "20px",
  fontWeight: "bold",
});

const EducationItemContainer = styled(Box)(({ isLastInSection }) => ({
  display: "flex",
  marginBottom: "20px",
  position: "relative",
  "& ::after": {
    content: "''",
    position: "absolute",
    left: "10px",
    top: "25px",
    bottom: "-20px",
    width: "2px",
    borderLeft: "2px dotted #250258",
  },
  ...(isLastInSection && {
    "&::after": {
      display: "none", // Hide the dotted line after the last item in each section
    },
  }),
}));

const Circle = styled(Box)({
  width: "20px",
  height: "20px",
  backgroundColor: "#250258",
  color: "#fff",
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontWeight: "bold",
  fontSize: "14px",
  position: "relative",
  zIndex: 1,
  marginRight: "10px",
});

const EducationContent = styled(Box)({
  flexGrow: 1,
  backgroundColor: "#fff5f5",
  padding: "10px 20px",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
});

const Degree = styled(Typography)({
  fontSize: "16px",
  fontFamily: "Urbane-Light",
  fontWeight: "bold",
  margin: 0,
  textTransform: "capitalize",
});
const ClientDetailsLink = styled(Typography)({
  fontSize: "16px",
  fontFamily: "Urbane-Light",
  fontWeight: "bold",
  margin: 0,
  textTransform: "capitalize",
  cursor: "pointer", // Make it look clickable
  "&:hover": {
    textDecoration: "underline", // Add hyperlink style on hover
    color: "#007bff", // Optional: Change color on hover
  },
});

const University = styled(Typography)({
  color: "#250258",
  fontSize: "14px",
  fontFamily: "Urbane-Light",
  marginBottom: "5px",
});

export const SectionHeader = ({ title, onEdit }) => {
  const navigate = useNavigate();
  return (
    <Box display="flex" alignItems="center" justifyContent={"end"}>
      <Tooltip title="Go Back" arrow>
        <IconButton color="primary">
          <ArrowBackIcon onClick={() => navigate(-1)} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const options = [
  "Engaged",
  "Interview",
  "Rejected",
  "Offer Declined",
  "Offer Extended",
  "Hired",
];

const CandidateViewDetail = ({
  setisView,
  selectedCandidate,
  isView,
  updateList,
  page,
  getallcandidatesData,
  isReport = false,
  fromAdminReport = false,
  reportName = "",
  DateFindBy = "",
  directDate = "",
  fromInprocess = false,
}) => {
  const [isClientDetail, setIsClientDetail] = useState(false);
  const [isInprocess, setIsInprocess] = useState(false);

  const handleClick = () => {
    if (fromInprocess === true) {
      setIsInprocess(true);
    } else {
      setIsClientDetail(true);
    }
  };
  const { user } = useSelector((state) => state.auth);
  const { candidates, loading } = useSelector((state) => state.candidate);
  const { _id, user_type } = user ?? {};

  const dispatch = useDispatch();
  const { can_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const result =
    Array.isArray(candidates) &&
    candidates?.find((item) => item._id.$oid === can_id);
  const candidateData = selectedCandidate ?? result ?? {};
  useEffect(() => {
    if (
      !loading &&
      (!candidateData || Object.keys(candidateData).length === 0)
    ) {
      navigate("/dashboard");
    }
  }, [loading, candidateData, navigate]);
  if (!candidateData || Object.keys(candidateData).length === 0) return null;

  const {
    candidate_firstname,
    candidate_lastname,
    candidate_email,
    candidate_contactno,
    candidate_education,
    candidate_university,
    candidate_anylocation,
    candidate_expertise,
    candidate_skills,
    candidate_certifications,
    candidate_company,
    candidate_resumeurl,
    candidate_notes,
    candidate_language_reference,
    candidate_compensation_to,
    candidate_compensation_from,
    isdownloaded,
    candidate_specifiedlocation,
    candidate_statusinfo,
    candidate_clientid,
    _id: candidateid,
    candidate_baseamount,
    candidate_bonusamount,
    candidate_experiance,
    candidate_totalamount,
    candidate_emplymentstatus,

    candidate_education_details,
    candidate_status,
    search_logId,
  } = candidateData;

  const search_id = searchParams.get("search_id") ?? search_logId?.$oid;
  const lastStatusInfo = candidate_statusinfo[candidate_statusinfo?.length - 1];

  const lastCompanyName = lastStatusInfo
    ? lastStatusInfo.client_companyname || lastStatusInfo.client_name
    : "N/A";
  const [selectedStatus, setSelectedStatus] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [open, setOpen] = useState(false);
  const [changeStatusModal, setChangeStatusModal] = useState(false);
  const [isloading, setIsLoading] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };
  const currentStatus =
    candidate_statusinfo?.[candidate_statusinfo.length - 1]?.status || "";
  const initialIndex = options?.findIndex(
    (option) => option.toLowerCase() === currentStatus.toLowerCase()
  );
  const [selectedIndex, setSelectedIndex] = React.useState(
    initialIndex !== -1 ? initialIndex : 0
  );
  async function UpdateStatus(data) {
    try {
      const { payload } = await dispatch(updateCandidateStatus(data));
      if (payload.status === 200) {
        const status = payload?.data?.status;
        const formattedStatus =
          status.charAt(0).toUpperCase() + status.slice(1);
        const selectedIndex = options.findIndex(
          (option) => option.toLowerCase() === formattedStatus.toLowerCase()
        );
        if (selectedIndex !== -1) {
          setSelectedIndex(selectedIndex);
        } else {
          console.log("Status not found in options");
        }
        if (updateList) {
          updateList("", page);
        }
        notifySuccess("Candidate status updated successfully!");
        setSelectedStatus("");
      } else {
        notifyError(payload.message);
      }
    } catch (error) {
      console.error(error);
    }
  }
  const handleUpdateStatus = async (status) => {
    const hireStatus =
      status === "Hired" ||
      status === "Offer Extended" ||
      status === "Rejected";

    if (hireStatus) {
      setOpen(true);
      setSelectedStatus(status);
    } else {
      let data = {
        clientid: _id?.$oid,
        candidateid: candidateid?.$oid,
        status: status,
        searchlogid: search_id,
        rejection_reason: "",
      };
      await UpdateStatus(data);
    }
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      candidate_totalamount: candidate_totalamount || 0,
      clientid: _id?.$oid,
      candidateid: candidateid?.$oid,
      status: selectedStatus,
      searchlogid: search_id,
      rejection_reason: "",
      other_reason: "",
    },
    validationSchema: Yup.object({
      ...(selectedStatus === "Hired" || selectedStatus === "Offer Extended"
        ? {
            candidate_totalamount: candidateTotalAmount,
          }
        : {}),
      ...(rejectionReason === "Other"
        ? { other_reason: userTextValidation("Reason is required") }
        : {}),
    }),
    onSubmit: async (values) => {
      let data = {
        ...values,
        rejection_reason:
          rejectionReason === "Other"
            ? values.other_reason
            : selectedStatus === "Rejected"
            ? rejectionReason
            : "",
      };
      let { other_reason, ...filter_data } = data;
      try {
        await UpdateStatus(filter_data);
      } catch (error) {
        console.error("Error during hiring:", error);
      } finally {
        handleClose();
      }
    },
  });
  const [open2, setOpen2] = useState(false);
  const onClose2 = () => {
    setOpen2(false);
    setIsClientDetail(false);
    setIsInprocess(false);
  };
  const ViewStatusModal = () => setOpen2(true);
  const handleDownloadResume = async () => {
    window.open(`${MEDIA_URL}${candidate_resumeurl}`, "_blank");
  };
  const handleChangeStatus = () => setChangeStatusModal(true);
  const onClose = () => setChangeStatusModal(false);
  const onConfirm = async () => {
    setIsLoading(true);
    let statusToSend;
    if (candidate_status === "active") {
      statusToSend = "inactive";
    } else if (candidate_status === "inactive") {
      statusToSend = "active";
    } else {
      notifyError("Cannot change status with the given value.");
      return;
    }
    try {
      let data = {
        userid: _id?.$oid,
        candidateid: candidateid?.$oid,
        status: statusToSend,
        usertype: "admin",
      };
      const { payload } = await dispatch(changeStatus(data));
      if (payload.status === 200) {
        getallcandidatesData();
        notifySuccess("Candidate status updated successfully!");
        setChangeStatusModal(false);
        setisView(false);
      }
    } catch (error) {
      notifyError("Something went wrong while changing the status.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    scrollToTop();
  }, []);

  if (isloading) return <Loader />;

  return (
    <>
      <Container maxWidth="lg" sx={{ my: 4 }}>
        <Box display="flex" alignItems="center" justifyContent={"end"}>
          <Tooltip title="Go Back" arrow>
            <IconButton color="primary">
              <ArrowBackIcon
                onClick={() => (isView ? setisView(false) : navigate(-1))}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <InfoContainer>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Info>{`${candidate_firstname} ${candidate_lastname}`}</Info>
              {fromAdminReport && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClick}
                    sx={{
                      fontFamily: "Urbane-light",
                    }}
                  >
                    View details
                  </Button>
                  <ClientDetailsDialogue
                    open={isClientDetail}
                    onClose={onClose2}
                    candidate_statusinfo={candidate_statusinfo}
                    candidateData={candidateData}
                    reportName={reportName}
                    DateFindBy={DateFindBy}
                    directDate={directDate}
                  />
                  <ClientDetailsDialogueForInprocessCandidate
                    open={isInprocess}
                    onClose={onClose2}
                    candidate_statusinfo={candidate_statusinfo}
                    candidateData={candidateData}
                    reportName={reportName}
                    DateFindBy={DateFindBy}
                    directDate={directDate}
                  />
                </>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <Box sx={{ marginRight: "24px" }}>
                  <InfoLabel>Mobile Number:</InfoLabel>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "8px",
                    }}
                  >
                    <FaPhoneAlt
                      style={{
                        height: "20px",
                        width: "20px",
                        color: "#696969",
                      }}
                    />
                    <InfoValue sx={{ marginLeft: "8px" }}>
                      {candidate_contactno}
                    </InfoValue>
                  </Box>
                </Box>

                <Box>
                  <InfoLabel>Email:</InfoLabel>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginTop: "8px",
                    }}
                  >
                    <MdEmail
                      style={{
                        height: "22px",
                        width: "22px",
                        color: "#696969",
                      }}
                    />
                    <InfoValue sx={{ marginLeft: "8px" }}>
                      {candidate_email}
                    </InfoValue>
                  </Box>
                </Box>
              </Box>
            </Grid>
            {
              <Grid item xs={12}>
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    padding: "10px",
                    transform: "translateY(-50%)",
                    margin: "20px",
                  }}
                >
                  {user_type === "admin" ? (
                    <Button
                      variant="text"
                      size="small"
                      color={"secondary"}
                      underline="none"
                      sx={{
                        fontWeight: "bold",
                        border: `1px solid red`,
                        borderRadius: "4px",
                        "&:hover": {
                          border: `1px solid darkred`,
                        },
                        mx: 1,
                      }}
                      onClick={handleDownloadResume}
                    >
                      <Tooltip title={"View Resume"} arrow>
                        <DownloadIcon sx={{ color: "red" }} />
                      </Tooltip>
                    </Button>
                  ) : (
                    <ResumeActionLink
                      row={candidateData}
                      handleAction={handleResumeAction}
                      dispatch={dispatch}
                      clientId={_id?.$oid}
                      searchLogId={search_id}
                    />
                  )}
                </Box>
              </Grid>
            }
          </Grid>
        </InfoContainer>

        {isReport === false && (
          <>
            {user_type === "admin" ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <EducationContent>
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <University>Hired By</University>
                      <Degree sx={{ marginLeft: "8px" }}>
                        {lastCompanyName || "N/A"}
                      </Degree>
                    </Box>

                    <Box>
                      {(candidate_status === "active" ||
                        candidate_status === "inactive") && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleChangeStatus}
                        >
                          Change Stage
                        </Button>
                      )}
                    </Box>
                  </Box>
                </EducationContent>
                <Box display="flex" justifyContent="center" gap={2} m={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={ViewStatusModal}
                    sx={{
                      fontFamily: "Urbane-light",
                    }}
                  >
                    View Stages
                  </Button>
                </Box>

                <ViewStatus
                  open={open2}
                  onClose={onClose2}
                  candidate_statusinfo={candidate_statusinfo}
                />
              </Box>
            ) : (
              <>
                {window?.location?.pathname !== "/client-reports" && (
                  <StatusContainer>
                    <Box display={"flex"} alignItems={"center"} a gap={5}>
                      <Typography variant="body1" fontSize={"14px"}>
                        Status
                      </Typography>

                      {candidate_status === "active" ? (
                        <ActionButton
                          variant="contained"
                          color="primary"
                          onClick={() => handleUpdateStatus("Engaged")}
                          disabled={loading}
                          startIcon={
                            loading ? <CircularProgress size={24} /> : null
                          }
                        >
                          Engage
                        </ActionButton>
                      ) : (
                        <CandidateStatusButton
                          handleUpdateStatus={handleUpdateStatus}
                          selectedIndex={selectedIndex}
                          initialIndex={initialIndex}
                        />
                      )}
                    </Box>
                  </StatusContainer>
                )}
              </>
            )}
          </>
        )}

        {/* {fromAdminReport && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <EducationContent>
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <University>{reportName}</University>
                  <ClientDetailsLink sx={{ marginLeft: "8px" }} onClick={handleClick}>
                    {lastCompanyName || "N/A"}
                  </ClientDetailsLink>

                </Box>
                <Box>
                  <University>Recruiter Name</University>
                  <Degree sx={{ marginLeft: "8px" }}>
                    {selectedCandidate?.recruiter_name || "N/A"}
                  </Degree>
                </Box>
                <Box>
                  <University>Search person</University>
                  <Degree sx={{ marginLeft: "8px" }}>
                    {selectedCandidate?.searchperson || "N/A"}
                  </Degree>
                </Box>
              </Box>
              <ClientDetailsDialogue
              open={isClientDetail}
              onClose={onClose2}
              candidate_statusinfo={candidate_statusinfo}
              reportName={reportName}
            />
            </EducationContent>
            <Box display="flex" justifyContent="center" gap={2} m={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={ViewStatusModal}
                sx={{
                  fontFamily: "Urbane-light",
                }}
              >
                View Status
              </Button>
            </Box>

            <ViewStatus
              open={open2}
              onClose={onClose2}
              candidate_statusinfo={candidate_statusinfo}
            />
          </Box>
        )} */}

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8} sx={{ padding: 0 }}>
              <Box>
                <EducationSectionContainer>
                  {/* Education Section */}
                  <InfoLabel>Education</InfoLabel>

                  {candidate_education_details?.map(
                    ({ candidate_education, candidate_university }) => (
                      <EducationItemContainer isLastInSection>
                        <Circle />
                        <EducationContent>
                          <Degree>{candidate_education}</Degree>
                          <University>{candidate_university}</University>
                        </EducationContent>
                      </EducationItemContainer>
                    )
                  )}
                  {/* <EducationItemContainer isLastInSection>
                    <Circle />
                    <EducationContent>
                      <Degree>Computer Science</Degree>
                      <University>Harvard University</University>
                    </EducationContent>
                  </EducationItemContainer> */}
                  {/* Work Experience Section */}
                  <InfoLabel>Work Experience</InfoLabel>
                  {candidate_company?.map((job, index) => (
                    <EducationItemContainer
                      key={index}
                      isLastInSection={index === candidate_company?.length - 1}
                    >
                      <Circle />
                      <EducationContent>
                        <Degree>{job.job_title}</Degree>
                        <University>{job.company_name}</University>
                        <Box>
                          <Typography variant="caption">
                            {job.from_year} -{" "}
                            {job.isPresent ? "Present" : job.to_year}
                          </Typography>

                          {job.job_description && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              gutterBottom
                            >
                              {job.job_description}
                            </Typography>
                          )}

                          {job.achievements && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              gutterBottom
                            >
                              <strong>Achievements:</strong> {job.achievements}
                            </Typography>
                          )}

                          {job.job_industry && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              gutterBottom
                            >
                              <strong>Industry:</strong> {job.job_industry}
                            </Typography>
                          )}
                        </Box>
                      </EducationContent>
                    </EducationItemContainer>
                  ))}

                  {candidate_certifications?.length > 0 && (
                    <>
                      <InfoLabel>Certifications</InfoLabel>
                      {candidate_certifications?.map((cert, index) => (
                        <EducationItemContainer
                          key={index}
                          isLastInSection={
                            index === candidate_certifications?.length - 1
                          }
                        >
                          <Circle />
                          <EducationContent>
                            <Degree>{cert.certification}</Degree>
                            <University>
                              Status: {cert.certification_status}
                            </University>
                          </EducationContent>
                        </EducationItemContainer>
                      ))}
                    </>
                  )}
                </EducationSectionContainer>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} sx={{ padding: 0 }}>
              <Box sx={{ mb: 2 }}>
                <RightInfoContainer>
                  <RightInfoItem>
                    <RightInfoRow>
                      <CalendarTodayIcon
                        style={{
                          height: "20px",
                          width: "20px",
                          color: "#696969",
                        }}
                      />
                      <RightInfoLabel>Experience:</RightInfoLabel>
                    </RightInfoRow>
                    <RightInfoValue>
                      {candidate_experiance === "less_than_1"
                        ? "Less than 1 year"
                        : candidate_experiance === "new_grad"
                        ? "New Grad"
                        : candidate_experiance + " years"}
                    </RightInfoValue>
                  </RightInfoItem>
                  <RightInfoItem>
                    <RightInfoRow>
                      <BadgeIcon
                        style={{
                          height: "20px",
                          width: "20px",
                          color: "#696969",
                        }}
                      />
                      <RightInfoLabel>Employment Status:</RightInfoLabel>
                    </RightInfoRow>
                    <RightInfoValue>{candidate_emplymentstatus}</RightInfoValue>
                  </RightInfoItem>

                  {/* Compensation Range */}
                  <RightInfoItem>
                    <RightInfoRow>
                      <MonetizationOnIcon
                        style={{
                          height: "20px",
                          width: "20px",
                          color: "#696969",
                        }}
                      />
                      <RightInfoLabel>Compensation Range:</RightInfoLabel>
                    </RightInfoRow>
                    <RightInfoValue>{`${formatUSAmount(
                      candidate_compensation_from
                    )} - ${formatUSAmount(
                      candidate_compensation_to
                    )}`}</RightInfoValue>
                  </RightInfoItem>

                  {/* Languages Proficiency */}
                  <RightInfoItem>
                    <RightInfoRow>
                      <TranslateOutlinedIcon
                        style={{
                          height: "20px",
                          width: "20px",
                          color: "#696969",
                        }}
                      />
                      <RightInfoLabel>Languages Proficiency:</RightInfoLabel>
                    </RightInfoRow>
                    {candidate_language_reference?.map((language) => (
                      <RightInfoValue key={language.value}>
                        {language.label}
                      </RightInfoValue>
                    ))}
                  </RightInfoItem>

                  {/* Location */}
                  <RightInfoItem>
                    <RightInfoRow>
                      <PlaceOutlinedIcon
                        style={{
                          height: "20px",
                          width: "20px",
                          color: "#696969",
                        }}
                      />
                      <RightInfoLabel>Geographic Preference:</RightInfoLabel>
                    </RightInfoRow>
                    <RightInfoValue>
                      {candidate_anylocation === "any"
                        ? "Any Location"
                        : candidate_specifiedlocation &&
                          candidate_specifiedlocation?.length > 0
                        ? candidate_specifiedlocation.map((location, index) => (
                            <span key={index}>
                              {location}
                              {index <
                                candidate_specifiedlocation?.length - 1 && (
                                <br />
                              )}
                            </span>
                          ))
                        : "Location not specified"}
                    </RightInfoValue>
                  </RightInfoItem>
                </RightInfoContainer>
              </Box>

              {/* Skills and Expertise Section */}
              <RightInfoContainer sx={{ width: "100%", mt: 2 }}>
                <Box display="flex" justifyContent="space-between">
                  <RightInfoItem>
                    <RightInfoRow>
                      <RightInfoLabel>Skills and Expertise:</RightInfoLabel>
                    </RightInfoRow>
                    <RightInfoValue>
                      {(candidate_expertise?.length > 0 ||
                        candidate_skills?.length > 0) && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {/* Render Expertise */}
                          {(candidate_expertise || [])?.map(
                            (expertise, index) =>
                              (expertise.label || expertise.value) && (
                                <Box
                                  key={`expertise-${index}`}
                                  sx={{
                                    backgroundColor: "white",
                                    color: "black",
                                    padding: "5px 10px",
                                    borderRadius: "10px",
                                    border: "1px solid #ddd",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                    fontSize: "14px",
                                  }}
                                >
                                  {expertise.label || expertise.value}
                                </Box>
                              )
                          )}

                          {/* Render Skills */}
                          {(candidate_skills || [])?.map(
                            (skill, index) =>
                              (skill.label || skill.value) && (
                                <Box
                                  key={`skill-${index}`}
                                  sx={{
                                    backgroundColor: "white",
                                    color: "black",
                                    padding: "5px 10px",
                                    borderRadius: "10px",
                                    border: "1px solid #ddd",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                    fontSize: "14px",
                                  }}
                                >
                                  {skill.label || skill.value}
                                </Box>
                              )
                          )}
                        </Box>
                      )}
                    </RightInfoValue>
                  </RightInfoItem>
                </Box>
              </RightInfoContainer>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <CandidateBonusDialog
        value={rejectionReason}
        setValue={setRejectionReason}
        selectedOptions={selectedStatus}
        open={open}
        onClose={handleClose}
        formik={formik}
      />
      <AnimatedDialog open={changeStatusModal} onClose={onClose}>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: "Urbane-light" }}>
            {/* {`Are you sure you want to ${
              candidate_status === "active" ? "Inactive" : "Active"
            } this candidate?`} */}

            {candidate_status === "active"
              ? "Are you sure you want to De-Activate this candidate?"
              : "Do you want to Activate this Candidate?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <CustomButton onClick={onConfirm} color="primary">
            Yes
          </CustomButton>
          <CustomButton onClick={onClose} color="error">
            No
          </CustomButton>
        </DialogActions>
      </AnimatedDialog>
    </>
  );
};
export default CandidateViewDetail;
